import { useState, useEffect } from "react";
import { Search, RefreshCw, MessageSquare, CheckCircle, Clock, Reply } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";

interface Feedback {
  id: string;
  user_id: string | null;
  subject: string;
  message: string;
  status: string;
  admin_reply: string | null;
  replied_at: string | null;
  created_at: string;
  user_nickname?: string;
}

export default function FeedbackManagement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFeedbacks = async () => {
    setIsLoading(true);

    const { data: feedbackData, error } = await supabase
      .from("feedback")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching feedbacks:", error);
      setIsLoading(false);
      return;
    }

    // Get user nicknames
    const userIds = [...new Set(feedbackData?.filter(f => f.user_id).map(f => f.user_id) || [])];
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("user_id, nickname")
      .in("user_id", userIds);

    const profilesMap = new Map(profilesData?.map(p => [p.user_id, p.nickname]));

    const enrichedFeedbacks = feedbackData?.map(feedback => ({
      ...feedback,
      user_nickname: feedback.user_id ? (profilesMap.get(feedback.user_id) || "未知用户") : "匿名",
    })) || [];

    setFeedbacks(enrichedFeedbacks);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const filteredFeedbacks = feedbacks.filter(feedback =>
    feedback.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feedback.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feedback.user_nickname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleReply = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setReplyText(feedback.admin_reply || "");
    setReplyDialogOpen(true);
  };

  const submitReply = async () => {
    if (!selectedFeedback || !replyText.trim()) return;

    setIsSubmitting(true);

    const { error } = await supabase
      .from("feedback")
      .update({
        admin_reply: replyText,
        replied_at: new Date().toISOString(),
        replied_by: user?.id,
        status: "replied",
      })
      .eq("id", selectedFeedback.id);

    if (error) {
      toast({
        title: "回复失败",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "回复成功" });
      setReplyDialogOpen(false);
      fetchFeedbacks();
    }

    setIsSubmitting(false);
  };

  const markAsResolved = async (feedbackId: string) => {
    const { error } = await supabase
      .from("feedback")
      .update({ status: "resolved" })
      .eq("id", feedbackId);

    if (error) {
      toast({ title: "操作失败", variant: "destructive" });
    } else {
      toast({ title: "已标记为已解决" });
      fetchFeedbacks();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />待处理</Badge>;
      case "replied":
        return <Badge variant="outline" className="bg-blue-100 text-blue-800"><Reply className="h-3 w-3 mr-1" />已回复</Badge>;
      case "resolved":
        return <Badge variant="outline" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />已解决</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">意见反馈管理</h1>
        <Button variant="outline" size="sm" onClick={fetchFeedbacks}>
          <RefreshCw className="h-4 w-4 mr-2" />
          刷新
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索主题、内容或用户..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">加载中...</div>
          ) : filteredFeedbacks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">暂无反馈记录</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>用户</TableHead>
                  <TableHead>主题</TableHead>
                  <TableHead className="max-w-xs">内容</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>提交时间</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFeedbacks.map((feedback) => (
                  <TableRow key={feedback.id}>
                    <TableCell className="font-medium">{feedback.user_nickname}</TableCell>
                    <TableCell>{feedback.subject}</TableCell>
                    <TableCell className="max-w-xs truncate">{feedback.message}</TableCell>
                    <TableCell>{getStatusBadge(feedback.status)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {format(new Date(feedback.created_at), "M月d日 HH:mm", { locale: zhCN })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReply(feedback)}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {feedback.admin_reply ? "查看回复" : "回复"}
                        </Button>
                        {feedback.status !== "resolved" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAsResolved(feedback.id)}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            解决
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>反馈详情</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">主题</p>
                <p className="font-medium">{selectedFeedback.subject}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">内容</p>
                <p className="text-sm bg-muted p-3 rounded-lg">{selectedFeedback.message}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">管理员回复</p>
                <Textarea
                  placeholder="输入回复内容..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={submitReply} disabled={isSubmitting || !replyText.trim()}>
              {isSubmitting ? "提交中..." : "提交回复"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
